import { useEffect, useRef } from 'react';

// Module-level ref-count of active body-scroll locks. Two modals open at
// once share the lock, and only the last one to close releases the body
// styles + restores scroll position. Without this, if modal A opens then
// modal B opens, then modal A closes first, B's effect cleanup would have
// already run because A's cleanup stomps the body styles back to default.
let activeLockCount = 0;
let lockedScrollY = 0;

const acquireBodyLock = () => {
  if (activeLockCount === 0 && globalThis.window !== undefined) {
    lockedScrollY = globalThis.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${lockedScrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  }
  activeLockCount += 1;
};

const releaseBodyLock = () => {
  activeLockCount = Math.max(0, activeLockCount - 1);
  if (activeLockCount === 0 && globalThis.window !== undefined) {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    globalThis.scrollTo(0, lockedScrollY);
  }
};

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Lifecycle/a11y companion hook for the codebase's modals. It supports BOTH
 * modal patterns currently in the tree, picking the path at runtime from the
 * element the consumer attaches `dialogRef` to:
 *
 *  • Native `<dialog>` (TD-2120 Item 2 — BulkExportConfirmModal,
 *    FallbackActivationModal, OnHoldActivationModal): opened with `showModal()`,
 *    so the platform owns focus trap, initial focus, focus restore, Escape,
 *    `aria-modal` and top-layer rendering. The hook fills only the gaps —
 *    lifecycle sync, the ref-counted scroll lock, forwarding the native
 *    `cancel`/`close` events to `onClose`, and backdrop light-dismiss.
 *
 *  • Legacy `role="dialog"` div in a Radix Portal (SessionLimitModal /
 *    TD-2239, EnrichmentDownloadDialog / TD-2099 — not migrated to `<dialog>`):
 *    the platform gives nothing, so the hook still provides Escape-to-close,
 *    initial focus on the first focusable (deferred a frame because Portal
 *    attaches async), a Tab/Shift+Tab focus trap, and focus restore on close.
 *    Those modals handle their own backdrop click.
 *
 * Detection is `typeof el.showModal === 'function'`: a real `<dialog>` has it,
 * a `<div role="dialog">` does not. The scroll lock is shared by both paths.
 * Contract is unchanged: `{ isOpen, onClose, dialogRef }`.
 */
const useModalA11y = ({ isOpen, onClose, dialogRef }) => {
  // Keep the latest onClose without re-running the lifecycle effect (which
  // would close+reopen the dialog) when a parent passes a fresh callback.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const previousFocusRef = useRef(null);

  // Body scroll lock — common to both patterns. `showModal()` makes the rest of
  // the page inert but does NOT stop it scrolling, and the legacy path never
  // did either, so the ref-counted lock applies regardless of mode.
  useEffect(() => {
    if (!isOpen) return undefined;
    acquireBodyLock();
    return releaseBodyLock;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const dialog = dialogRef?.current;
    const isNative = !!dialog && typeof dialog.showModal === 'function';

    // ---- Native <dialog> path ----
    if (isNative) {
      if (!dialog.open) dialog.showModal();

      const handleCancel = (event) => {
        // preventDefault so React (via onClose) drives the unmount, keeping
        // `isOpen` the single source of truth.
        event.preventDefault();
        onCloseRef.current?.();
      };
      // Backdrop light-dismiss: a click whose target is the <dialog> itself —
      // i.e. the ::backdrop, since content is wrapped and the dialog has no
      // padding.
      const handleClick = (event) => {
        if (event.target === dialog) onCloseRef.current?.();
      };
      // Keep React in sync if the dialog is closed by something other than our
      // controlled path (e.g. a <form method="dialog"> submit). Removed before
      // our own teardown close() so cleanup never re-enters onClose.
      const handleClose = () => {
        onCloseRef.current?.();
      };
      dialog.addEventListener('cancel', handleCancel);
      dialog.addEventListener('click', handleClick);
      dialog.addEventListener('close', handleClose);

      return () => {
        dialog.removeEventListener('cancel', handleCancel);
        dialog.removeEventListener('click', handleClick);
        dialog.removeEventListener('close', handleClose);
        if (dialog.open) dialog.close();
      };
    }

    // ---- Legacy role="dialog" path (Portal-rendered div) ----
    // Escape-to-close, captured on the window so it wins over inner handlers.
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current?.();
      }
    };
    globalThis.addEventListener('keydown', handleEscape, { capture: true });

    // Tab / Shift+Tab focus trap, cycling within the dialog.
    const handleTab = (event) => {
      if (event.key !== 'Tab' || !dialogRef?.current) return;
      const focusables = dialogRef.current.querySelectorAll(FOCUSABLE_SELECTOR);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleTab);

    // Capture the trigger before any ref gate (Portal mounts the dialog node
    // async in jsdom, so dialogRef.current can still be null here). Skip
    // capture when focus is already inside another open dialog so stacked
    // modals don't bounce focus back into a closing parent. Initial focus is
    // deferred a frame to land after Portal's second render.
    if (!previousFocusRef.current) {
      const active = document.activeElement;
      const otherDialog = active?.closest?.('[role="dialog"]');
      const isInsideOtherDialog = otherDialog && otherDialog !== dialogRef?.current;
      previousFocusRef.current = isInsideOtherDialog ? null : active;
    }
    const rafId = requestAnimationFrame(() => {
      if (!dialogRef?.current) return;
      const firstFocusable = dialogRef.current.querySelector(FOCUSABLE_SELECTOR);
      (firstFocusable ?? dialogRef.current).focus();
    });

    return () => {
      globalThis.removeEventListener('keydown', handleEscape, { capture: true });
      document.removeEventListener('keydown', handleTab);
      cancelAnimationFrame(rafId);
      const target = previousFocusRef.current;
      previousFocusRef.current = null;
      target?.focus?.();
    };
  }, [isOpen, dialogRef]);
};

// Test-only escape hatch. The body-lock ref-count is module-level state; if a
// test aborts mid-render the count can strand above zero and leak into the next
// test. Tests call this in afterEach to guarantee a clean slate. Not for
// production use.
export const __resetModalLockState = () => {
  activeLockCount = 0;
  lockedScrollY = 0;
};

export default useModalA11y;
